import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { PullToRefreshIndicatorComponent } from './pull-to-refresh-indicator/pull-to-refresh-indicator.component';

const MAX_DRAG_DISTANCE = 120;
const LOADING_DRAG_POSITION = 60;

@Component({
  selector: 'soft-pull-to-refresh-container',
  imports: [CommonModule, PullToRefreshIndicatorComponent],
  templateUrl: './pull-to-refresh-container.component.html',
  styleUrls: ['./pull-to-refresh-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PullToRefreshContainerComponent {
  @Input() canRefresh = false;
  @Output() refresh = new EventEmitter<() => void>();

  dragDistance = 0;
  isRefreshing = false;

  private dragStart?: number;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  @HostListener('touchstart', ['$event'])
  startDragging(event: TouchEvent): void {
    const scrollTop = this.elementRef.nativeElement.scrollTop;
    const isOnTop = scrollTop === 0;
    if (!isOnTop || !this.canRefresh || this.isRefreshing) return;
    this.dragStart = event.touches[0].pageY;
  }

  @HostListener('touchmove', ['$event'])
  onDragging(event: TouchEvent): void {
    if (this.dragStart === undefined || !this.canRefresh || this.isRefreshing) return;
    const distance = event.touches[0].pageY - this.dragStart;
    this.dragDistance = Math.max(0, Math.min(distance, MAX_DRAG_DISTANCE));
  }

  @HostListener('touchend')
  endDragging(): void {
    if (this.dragStart === undefined || !this.canRefresh || this.isRefreshing) return;
    if (this.dragDistance >= LOADING_DRAG_POSITION) {
      this.execute();
    } else {
      this.reset();
    }
  }

  get transformWhileDragging(): object {
    return this.dragDistance > 0
      ? { transform: `translateY(${this.dragDistance}px)` }
      : {};
  }

  private execute(): void {
    if (!this.canRefresh || this.refresh.observers.length === 0) return;
    this.dragDistance = LOADING_DRAG_POSITION;
    this.isRefreshing = true;
    this.refresh.emit();
    this.reset();
  }

  private reset(): void {
    this.dragStart = undefined;
    this.isRefreshing = false;
    this.dragDistance = 0;
    this.changeDetectorRef.markForCheck();
  }
}
